import {DateTime} from 'luxon';
import {connect} from 'react-redux';

import {FrameTypesEnum} from '../../../../../libs/shared/core/src/models/api/apiTypes';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {getIsUserGlobalAdmin} from '../../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {
  getHasFluSetupGuide,
  getHasP4StartGuideHidden,
} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {wrapActionCreator} from '../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {OnboardingTipTypesEnum, StartGuideTaskTypesEnum} from '../../../../models/api/preferencesModel';
import {Dispatch} from '../../../../store/appStore';
import {updateSelectedSideFrameIdAction} from '../../../../store/data/applications/selectedSideFrameIdActions';
import {makeGetSelectedSideFrameId} from '../../../../store/data/applications/selectedSideFrameIdSelectors';
import {updateStartGuideTasksAction} from '../../../../store/data/preferences/preferencesActions';
import {
  getCompletedStartGuideTasks,
  getSeenOnboardingTips,
  getWasOnboardingCompleted,
} from '../../../../store/data/preferences/preferencesSelectors';
import {getIsUserFirstGlobalAdmin, getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {makeGetAppPanelIdFromPanel} from '../../../../store/panels/panelsSelectors';
import {getIsStartGuideIncomplete} from '../../../../store/stateless/onboardingSelectors';
import {StartGuideTasksBanner} from './startGuideTasksBanner';

/*
 * Redux.
 */

interface StartGuideTasksBannerContainerProps {
  panelId: InboxesPanelId;
}

const mapStateToProps = makeMapStateToProps<StartGuideTasksBannerContainerProps>()(() => ({
  user: getUser,
  seenOnboardingTips: getSeenOnboardingTips,
  hasCompletedOnboarding: getWasOnboardingCompleted,
  isUserFirstGlobalAdmin: getIsUserFirstGlobalAdmin,
  isAdmin: getIsUserGlobalAdmin,
  completedStartGuideTasks: getCompletedStartGuideTasks,
  isStartGuideIncomplete: getIsStartGuideIncomplete,
  selectedSideFrameId: makeGetSelectedSideFrameId(makeGetAppPanelIdFromPanel()),
  hasP4StartGuideHidden: getHasP4StartGuideHidden,
  hasFluSetupGuide: getHasFluSetupGuide,
}));

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    updateStartGuideTasksAction: wrapActionCreator(dispatch, updateStartGuideTasksAction),
    updateSelectedSideFrameIdAction: wrapActionCreator(dispatch, updateSelectedSideFrameIdAction),
  };
}

export const StartGuideTasksBannerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const newUserTaskCuttoffTime = DateTime.local().minus({weeks: 4}).toMillis();
    const newUserCreatedAtEarliestReminderTime = DateTime.local().minus({days: 3}).toMillis();

    // We show start guide tasks for the first two weeks and shouldn't show the reminder until the account
    // at least a day old.
    const isUserNewUser =
      stateProps.user.createdAt >= newUserTaskCuttoffTime &&
      stateProps.user.createdAt < newUserCreatedAtEarliestReminderTime;

    // Check if the P4 start guide is already open or if we've seen the info bubble in the start guide.
    const hasSeenReminderInfoBubble =
      stateProps.selectedSideFrameId === FrameTypesEnum.START_GUIDE ||
      stateProps.seenOnboardingTips.includes(OnboardingTipTypesEnum.HELP_CENTER_TASKS);

    const shouldShowStartGuideBanner =
      !stateProps.hasFluSetupGuide &&
      !stateProps.hasP4StartGuideHidden &&
      !hasSeenReminderInfoBubble &&
      stateProps.seenOnboardingTips.includes(OnboardingTipTypesEnum.COMMENT_COMPOSER) &&
      stateProps.hasCompletedOnboarding &&
      !stateProps.isUserFirstGlobalAdmin &&
      stateProps.isStartGuideIncomplete &&
      isUserNewUser &&
      !stateProps.completedStartGuideTasks.includes(StartGuideTaskTypesEnum.HELP_CENTER_REMINDER_DISMISS);

    const onDismiss = () =>
      dispatchProps.updateStartGuideTasksAction(StartGuideTaskTypesEnum.HELP_CENTER_REMINDER_DISMISS);

    const onOpenP4 = () => {
      onDismiss();
      trackInterfaceEvent(InterfaceEventsEnum.HELP_CENTER_P1_REMINDER_CLICK, {
        type: 'getting_started',
        user_track: stateProps.isAdmin ? 'admin' : 'non_admin',
      });
      dispatchProps.updateSelectedSideFrameIdAction(FrameTypesEnum.START_GUIDE, ownProps.panelId);
    };

    return {
      ...stateProps,
      shouldShowStartGuideBanner,
      onDismiss,
      onOpenP4,
    };
  },
)(StartGuideTasksBanner);
